import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { LearningContentAPI } from '@chancenwerk/campus-learning-content/api/LearningContentAPI.js';
import type { CampusExercise } from '@chancenwerk/campus-learning-content/entities/CampusExercise.js';
import { CampusLevumiLevel } from '@chancenwerk/campus-learning-content/entities/levumi/CampusLevumiLevel.js';
import '@chancenwerk/components/chancen-component-card-title.js';
import { LevumiMathCategory, LevumiSubject } from '@chancenwerk/db/enums/CampusLevumiEnums.js';
import { ExerciseType } from '@chancenwerk/db/enums/enums.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { switchMap, takeUntil } from 'rxjs';
import { CampusComponentExerciseEdit } from './campus-page-exercise-edit.js';

/**
 * @customElement campus-page-exercise-edit-levumi
 */
@customElement('campus-page-exercise-edit-levumi')
export class CampusPageExerciseEditLEVUMI extends CampusComponentExerciseEdit {
  static override exerciseType: number = ExerciseType.LEVUMIGERMAN;
  static override exerciseName: string = 'LEVUMI Aufgabe';
  static override exerciseElement: string = 'campus-page-exercise-edit-levumi';

  @state() private readonly exerciseType = new RXController(this, 0);
  @state() private readonly _levumiLevels = new RXController(
    this,
    this.exerciseType.observable.pipe(
      switchMap(type =>
        LearningContentAPI.levumi.subscribeLevelsBySubject(this.getLevumiSubjectForLevumiExerciseType(type))(
          CampusLevumiLevel.allFields,
        ),
      ),
    ),
  );

  protected override getExcersiseType(): number {
    return this.exercise.value?.type ?? 0;
  }

  protected override getExcersieName(): string {
    return `LEVUMI (${this.getStringForLevumiExerciseType(this.exercise.value?.type ?? 0)}) Aufgabe`;
  }

  constructor() {
    super();
  }

  override connectedCallback(): void {
    super.connectedCallback();

    this.exercise.observable.pipe(takeUntil(this.disconnected)).subscribe(exercise => {
      if (exercise && !exercise.type) {
        exercise.type = this.getExcersiseType();
      }
      this.exerciseType.next(this.getExcersiseType());
    });
  }

  protected override async handleFormSuccess(exercise: CampusExercise): Promise<void> {
    if (!exercise.levumiLevelID) {
      return this.errorMsg.next('Für eine Levumi Aufgabe wird das Aufgabenlevel benötigt');
    }

    return super.handleFormSuccess(exercise);
  }

  override content() {
    return html`
      ${this.errorMsg.value ? html`<cr-text ${css({ color: 'red' })}>${this.errorMsg.value}</cr-text>` : nothing}
      <cr-card space="md" padding="md">
        <cr-flex horizontal space="md">
          <cr-flex flex space="md">
            <cr-flex space="md" space-between>
              <cr-text>Felder mit einem * müssen ausgefüllt werden!</cr-text>
              <cr-input ${this.form.field('title')} placeholder="Titel *"></cr-input>
              ${this.getExcersiseType() === 6
                ? html`
                    <cr-dropdown-selection
                      hide-add-button
                      placeholder="Kategorie *"
                      ${this.form.field('levumiMathCategory')}
                      .selectables=${Object.values(LevumiMathCategory)}
                      @value-picked=${() => this.requestUpdate()}
                      flex
                    ></cr-dropdown-selection>
                  `
                : nothing}
              ${this.getExcersiseType() === 0
                ? nothing
                : html`
                    <cr-dropdown-selection
                      hide-add-button
                      id="levelDropdown"
                      placeholder=${this.getExcersiseType() === 6 ? 'Station *' : 'Level *'}
                      ${this.form.field('levumiLevelID')}
                      .selectables=${this._filterStations(this._levumiLevels.value ?? [])
                        .sort((a, b) => (a.level.trim().toUpperCase() < b.level.trim().toUpperCase() ? -1 : 1))
                        .map(l => l._id)}
                      .renderString=${(levelID: string) =>
                        this._levumiLevels.value?.find(lev => lev._id === levelID)?.level}
                      flex
                    ></cr-dropdown-selection>
                  `}
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-card>
    `;
  }

  private _filterStations(levels: CampusLevumiLevel[]) {
    return levels.filter(l => {
      if (this.exercise.value?.levumiMathCategory !== LevumiMathCategory.MUL && l.level === 'Station 6') return false;
      if (this.exercise.value?.levumiMathCategory !== LevumiMathCategory.MUL && l.level === 'Station 5') return false;
      return true;
    });
  }

  private getStringForLevumiExerciseType(type: ExerciseType) {
    switch (type) {
      case ExerciseType.LEVUMIGERMAN:
        return 'Deutsch';
      case ExerciseType.LEVUMIMATH:
        return 'Mathematik';
      case ExerciseType.LEVUMI_NUMBER_DICTATE:
        return 'Zahlendiktat';
      default:
        return '';
    }
  }

  private getLevumiSubjectForLevumiExerciseType(type: ExerciseType) {
    switch (type) {
      case ExerciseType.LEVUMIGERMAN:
        return LevumiSubject.GERMAN;
      case ExerciseType.LEVUMIMATH:
        return LevumiSubject.MATH;
      case ExerciseType.LEVUMI_NUMBER_DICTATE:
        return LevumiSubject.NUMBER_DICTATION;
      default:
        return '';
    }
  }
}

/**
 * @customElement campus-page-exercise-edit-levumi-german
 */
@customElement('campus-page-exercise-edit-levumi-german')
export class CampusPageExerciseEditLEVUMIGerman extends CampusPageExerciseEditLEVUMI {
  static override exerciseType: number = ExerciseType.LEVUMIGERMAN;
  static override exerciseName: string = 'LEVUMI Deutsch Aufgabe';
  static override exerciseElement: string = 'campus-page-exercise-edit-levumi-german';

  protected override getExcersiseType(): number {
    return CampusPageExerciseEditLEVUMIGerman.exerciseType;
  }

  protected override getExcersieName(): string {
    return CampusPageExerciseEditLEVUMIGerman.exerciseName;
  }
}

/**
 * @customElement campus-page-exercise-edit-levumi-math
 */
@customElement('campus-page-exercise-edit-levumi-math')
export class CampusPageExerciseEditLEVUMIMath extends CampusPageExerciseEditLEVUMI {
  static override exerciseType: number = ExerciseType.LEVUMIMATH;
  static override exerciseName: string = 'LEVUMI Mathematik Aufgabe';
  static override exerciseElement: string = 'campus-page-exercise-edit-levumi-math';

  protected override getExcersiseType(): number {
    return CampusPageExerciseEditLEVUMIMath.exerciseType;
  }

  protected override getExcersieName(): string {
    return CampusPageExerciseEditLEVUMIMath.exerciseName;
  }
}

/**
 * @customElement campus-page-exercise-edit-levumi-number-dictate
 */
@customElement('campus-page-exercise-edit-levumi-number-dictate')
export class CampusPageExerciseEditLEVUMINumberDictate extends CampusPageExerciseEditLEVUMI {
  static override exerciseType: number = ExerciseType.LEVUMI_NUMBER_DICTATE;
  static override exerciseName: string = 'LEVUMI Zahlendiktat Aufgabe';
  static override exerciseElement: string = 'campus-page-exercise-edit-levumi-number-dictate';

  protected override getExcersiseType(): number {
    return CampusPageExerciseEditLEVUMINumberDictate.exerciseType;
  }

  protected override getExcersieName(): string {
    return CampusPageExerciseEditLEVUMINumberDictate.exerciseName;
  }
}
